// A noticeboard — a roofed wooden board on two posts with a few pinned notes,
// where couriers leave reports and the player reads them. Ported 1:1 from
// age-of-mcp's noticeboard.ts: the shared foundation comes from the library, the
// posts and board are local helpers, and the four build stages are transcribed
// from stage(s).
import * as THREE from "three";
import { defineModel, foundationSlab, type Part } from "../../lib";

const POST = "#6b4a26"; // posts / frame
const BOARD = "#8a5f34"; // cork/timber board
const ROOF = "#5e3f24"; // little shingle roof
const NOTE1 = "#efe6cf"; // pinned papers
const NOTE2 = "#dfe7ef";
const PIN = "#b23a34";

function posts(): Part[] {
  return [-0.13, 0.13].map((x) => ({
    geometry: new THREE.CylinderGeometry(0.03, 0.03, 0.5, 6),
    color: POST,
    position: [x, 0.25 + 0.14, 0] as [number, number, number],
  }));
}

function board(): Part {
  return { geometry: new THREE.BoxGeometry(0.36, 0.26, 0.04), color: BOARD, position: [0, 0.45, 0] };
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.34, 0.06)];
  if (s === 1) return [foundationSlab(0.34, 0.06), ...posts()];
  if (s === 2) return [foundationSlab(0.34, 0.06), ...posts(), board()];
  return [
    foundationSlab(0.34, 0.06),
    ...posts(),
    board(),
    // A small pitched roof over the board.
    { geometry: new THREE.BoxGeometry(0.44, 0.04, 0.16), color: ROOF, position: [0, 0.6, 0.05], rotation: [0.5, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.44, 0.04, 0.16), color: ROOF, position: [0, 0.6, -0.05], rotation: [-0.5, 0, 0] },
    // Pinned notes on the board face (+z).
    { geometry: new THREE.BoxGeometry(0.1, 0.12, 0.01), color: NOTE1, position: [-0.07, 0.47, 0.025], rotation: [0, 0, 0.1] },
    { geometry: new THREE.BoxGeometry(0.09, 0.1, 0.01), color: NOTE2, position: [0.08, 0.44, 0.025], rotation: [0, 0, -0.12] },
    { geometry: new THREE.BoxGeometry(0.02, 0.02, 0.02), color: PIN, position: [-0.07, 0.52, 0.03] },
    { geometry: new THREE.BoxGeometry(0.02, 0.02, 0.02), color: PIN, position: [0.08, 0.48, 0.03] },
  ];
}

export const noticeboard = defineModel({
  id: "noticeboard",
  tiles: { w: 1, h: 1 },
  batchId: "standard",
  name: "Noticeboard",
  tags: ["building", "noticeboard", "low-poly", "age-of-mcp", "classic"],
  summary: "Low-poly noticeboard.",
  description: "A posting board where couriers leave written reports to be read.",
  author: "age-of-mcp",
  footprint: 0.3,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
